'use client';

import { Badge, cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@newfront-insurance/core-ui/v2';
import type { LucideIcon } from 'lucide-react';
import { BrainIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { createContext, memo, useContext, useEffect, useMemo, useRef } from 'react';

import { useAutoCloseCollapsible } from './use-auto-close-collapsible';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ChainOfThoughtContextValue {
  isStreaming: boolean;
}

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(null);

const useChainOfThought = () => {
  const context = useContext(ChainOfThoughtContext);
  if (!context) {
    throw new Error('ChainOfThought components must be used within ChainOfThought');
  }
  return context;
};

// ---------------------------------------------------------------------------
// ChainOfThought (root) — single Collapsible wrapping trigger + content
// ---------------------------------------------------------------------------

export type ChainOfThoughtProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function ChainOfThought({
  className,
  isStreaming = false,
  open,
  defaultOpen = false,
  onOpenChange,
  children,
  ...props
}: ChainOfThoughtProps) {
  const { isOpen, handleOpenChange } = useAutoCloseCollapsible({
    isStreaming,
    open,
    defaultOpen,
    onOpenChange,
  });

  const contextValue = useMemo(() => ({ isStreaming }), [isStreaming]);

  return (
    <ChainOfThoughtContext.Provider value={contextValue}>
      <Collapsible
        className={cn('not-prose mb-4 w-full', className)}
        onOpenChange={handleOpenChange}
        open={isOpen}
        {...props}
      >
        {children}
      </Collapsible>
    </ChainOfThoughtContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// ChainOfThoughtHeader (CollapsibleTrigger)
// ---------------------------------------------------------------------------

export type ChainOfThoughtHeaderProps = ComponentProps<typeof CollapsibleTrigger> & {
  icon?: LucideIcon;
};

export const ChainOfThoughtHeader = memo(
  ({ className, children, icon: HeaderIcon = BrainIcon, ...props }: ChainOfThoughtHeaderProps) => {
    const { isStreaming } = useChainOfThought();

    return (
      <CollapsibleTrigger
        className={cn(
          'text-muted-foreground -mx-1 flex w-fit max-w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-sm leading-5 transition-colors',
          isStreaming ? 'animate-pulse' : 'hover:text-foreground hover:bg-muted/45',
          className,
        )}
        {...props}
      >
        <span className="border-border/60 bg-muted/35 flex size-5 shrink-0 items-center justify-center rounded-[5px] border text-current">
          <HeaderIcon className="size-3.5" />
        </span>
        <span className="min-w-0 truncate text-left font-medium">{children ?? 'Chain of Thought'}</span>
        <ChevronDownIcon className="size-3.5 shrink-0 opacity-70 transition-transform [[data-state=open]>&]:rotate-180" />
      </CollapsibleTrigger>
    );
  },
);

// ---------------------------------------------------------------------------
// ChainOfThoughtStepToggle — inline trigger for expanding previous steps
// ---------------------------------------------------------------------------

export type ChainOfThoughtStepToggleProps = ComponentProps<typeof CollapsibleTrigger> & {
  count: number;
};

export const ChainOfThoughtStepToggle = memo(({ className, count, ...props }: ChainOfThoughtStepToggleProps) => (
  <CollapsibleTrigger
    className={cn(
      'text-muted-foreground hover:text-foreground flex items-center gap-2 text-xs transition-colors',
      className,
    )}
    {...props}
  >
    <div className="flex w-4 shrink-0 items-center justify-center">
      <ChevronRightIcon className="size-3 transition-transform [[data-state=open]>&]:rotate-90" />
    </div>
    <span className="[[data-state=open]>&]:hidden">
      {count} previous {count === 1 ? 'step' : 'steps'}
    </span>
    <span className="[[data-state=closed]>&]:hidden">Hide previous {count === 1 ? 'step' : 'steps'}</span>
  </CollapsibleTrigger>
));

// ---------------------------------------------------------------------------
// ChainOfThoughtConnector — vertical line spacer between steps
// ---------------------------------------------------------------------------

export const ChainOfThoughtConnector = memo(({ className, ...props }: ComponentProps<'div'>) => (
  <div className={cn('flex gap-2', className)} {...props}>
    <div className="relative w-4 shrink-0">
      <div className="bg-border absolute top-0 bottom-0 left-1/2 -mx-px w-px" />
      <div className="h-3" />
    </div>
  </div>
));

// ---------------------------------------------------------------------------
// ChainOfThoughtStep
// ---------------------------------------------------------------------------

export type ChainOfThoughtStepProps = ComponentProps<'div'> & {
  label: ReactNode;
  description?: ReactNode;
  status?: 'complete' | 'active' | 'pending';
};

const stepStatusStyles = {
  active: 'text-muted-foreground',
  complete: 'text-muted-foreground',
  pending: 'text-muted-foreground/50',
};

export const ChainOfThoughtStep = memo(
  ({ className, label, description, status = 'complete', children, ...props }: ChainOfThoughtStepProps) => {
    const stepRef = useRef<HTMLDivElement>(null);

    // Set pulse animation imperatively so React re-renders never restart it.
    useEffect(() => {
      const el = stepRef.current;
      if (!el) return;
      if (status === 'active') {
        el.style.animation = 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite';
      }
      return () => {
        if (el) el.style.animation = '';
      };
    }, [status]);

    return (
      <div
        ref={stepRef}
        className={cn(
          'flex gap-2 text-sm',
          stepStatusStyles[status],
          'fade-in-0 slide-in-from-top-2 animate-in',
          className,
        )}
        {...props}
      >
        <div className="min-w-0 flex-1 space-y-2">
          {label && <div>{label}</div>}
          {description && <div className="text-muted-foreground">{description}</div>}
          {children}
        </div>
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// ChainOfThoughtContent (CollapsibleContent)
// ---------------------------------------------------------------------------

export type ChainOfThoughtContentProps = ComponentProps<typeof CollapsibleContent> & {
  showConnector?: boolean;
};

export const ChainOfThoughtContent = memo(
  ({ className, children, showConnector = true, ...props }: ChainOfThoughtContentProps) => (
    <CollapsibleContent
      className={cn(
        'mt-1',
        'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
        'text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in outline-none',
        className,
      )}
      {...props}
    >
      {showConnector && (
        <div className="flex gap-2">
          <div className="relative w-4 shrink-0">
            <div className="bg-border absolute top-0 bottom-0 left-1/2 -mx-px w-px" />
            <div className="h-3" />
          </div>
        </div>
      )}
      {children}
    </CollapsibleContent>
  ),
);

// ---------------------------------------------------------------------------
// ChainOfThoughtSearchResults / SearchResult / Image
// ---------------------------------------------------------------------------

export type ChainOfThoughtSearchResultsProps = ComponentProps<'div'>;

export const ChainOfThoughtSearchResults = memo(({ className, ...props }: ChainOfThoughtSearchResultsProps) => (
  <div className={cn('flex flex-wrap items-center gap-2', className)} {...props} />
));

export type ChainOfThoughtSearchResultProps = ComponentProps<typeof Badge>;

export const ChainOfThoughtSearchResult = memo(({ className, children, ...props }: ChainOfThoughtSearchResultProps) => (
  <Badge className={cn('gap-1 px-2 py-0.5 text-xs font-normal', className)} variant="secondary" {...props}>
    {children}
  </Badge>
));

export type ChainOfThoughtImageProps = ComponentProps<'div'> & {
  caption?: string;
};

export const ChainOfThoughtImage = memo(({ className, children, caption, ...props }: ChainOfThoughtImageProps) => (
  <div className={cn('mt-2 space-y-2', className)} {...props}>
    <div className="bg-muted relative flex max-h-[22rem] items-center justify-center overflow-hidden rounded-lg p-3">
      {children}
    </div>
    {caption && <p className="text-muted-foreground text-xs">{caption}</p>}
  </div>
));

// ---------------------------------------------------------------------------
// Display names
// ---------------------------------------------------------------------------

ChainOfThought.displayName = 'ChainOfThought';
ChainOfThoughtHeader.displayName = 'ChainOfThoughtHeader';
ChainOfThoughtStepToggle.displayName = 'ChainOfThoughtStepToggle';
ChainOfThoughtConnector.displayName = 'ChainOfThoughtConnector';
ChainOfThoughtStep.displayName = 'ChainOfThoughtStep';
ChainOfThoughtSearchResults.displayName = 'ChainOfThoughtSearchResults';
ChainOfThoughtSearchResult.displayName = 'ChainOfThoughtSearchResult';
ChainOfThoughtContent.displayName = 'ChainOfThoughtContent';
ChainOfThoughtImage.displayName = 'ChainOfThoughtImage';
